import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Plus, Edit, Trash2, User, RotateCcw } from 'lucide-react';
import { THERAPISTS } from '@/constants/testIds';
import { toast } from 'sonner';
import FileUpload from '@/components/FileUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Avatar component for therapist photos
const TherapistAvatar = ({ photoFileId, name }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPhoto = async () => {
      if (photoFileId) {
        try {
          const response = await axios.get(`${API}/files/${photoFileId}`, {
            withCredentials: true,
            responseType: 'blob'
          });
          const url = URL.createObjectURL(response.data);
          setPhotoUrl(url);
        } catch (error) {
          console.error('Error loading photo:', error);
        }
      }
      setLoading(false);
    };

    fetchPhoto();

    return () => {
      if (photoUrl) {
        URL.revokeObjectURL(photoUrl);
      }
    };
  }, [photoFileId]);

  if (loading && photoFileId) {
    return (
      <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
    );
  }

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-gray-200"
      />
    );
  }

  return (
    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
      <User className="w-6 h-6 text-gray-500" />
    </div>
  );
};

const TherapistsPage = () => {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTherapist, setEditingTherapist] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [trashedTherapists, setTrashedTherapists] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialization: '',
    photo_file_id: null,
  });

  useEffect(() => {
    fetchTherapists();
  }, []);

  const fetchTherapists = async () => {
    try {
      const { data } = await axios.get(`${API}/therapists`, { withCredentials: true });
      setTherapists(data || []);
    } catch (error) {
      console.error('Error fetching therapists:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar terapeutas');
      setTherapists([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTherapist) {
        await axios.put(`${API}/therapists/${editingTherapist.id}`, formData, { withCredentials: true });
        toast.success('Terapeuta actualizado');
      } else {
        await axios.post(`${API}/therapists`, formData, { withCredentials: true });
        toast.success('Terapeuta creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchTherapists();
    } catch (error) {
      toast.error('Error al guardar terapeuta');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Enviar este terapeuta a la papelera? Podrás restaurarlo durante 30 días.')) return;
    try {
      await axios.delete(`${API}/therapists/${id}`, { withCredentials: true });
      toast.success('Terapeuta movido a la papelera');
      fetchTherapists();
      if (showTrash) fetchTrash();
    } catch (error) {
      console.error('Error deleting therapist:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar terapeuta');
    }
  };

  const fetchTrash = async () => {
    try {
      const { data } = await axios.get(`${API}/therapists/trash`, { withCredentials: true });
      setTrashedTherapists(data || []);
    } catch (error) {
      setTrashedTherapists([]);
    }
  };

  const toggleTrash = () => {
    const next = !showTrash;
    setShowTrash(next);
    if (next) fetchTrash();
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/therapists/${id}/restore`, {}, { withCredentials: true });
      toast.success('Terapeuta restaurado');
      fetchTrash();
      fetchTherapists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    }
  };

  const handlePurge = async (id) => {
    if (!window.confirm('¿Eliminar este terapeuta DEFINITIVAMENTE? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/therapists/${id}/purge`, { withCredentials: true });
      toast.success('Terapeuta eliminado permanentemente');
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', specialization: '', photo_file_id: null });
    setEditingTherapist(null);
  };

  const openEditDialog = (therapist) => {
    setEditingTherapist(therapist);
    setFormData({
      name: therapist.name || '',
      email: therapist.email || '',
      phone: therapist.phone || '',
      specialization: therapist.specialization || '',
      photo_file_id: therapist.photo_file_id || null,
    });
    setDialogOpen(true);
  };

  if (loading) return <div>Cargando...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
            Terapeutas
          </h1>
          <p className="text-sm mt-1" style={{ color: '#718096' }}>
            Gestiona el equipo de profesionales
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showTrash ? 'default' : 'outline'}
            onClick={toggleTrash}
            className="flex items-center gap-2"
            data-testid="therapists-trash-toggle"
          >
            <Trash2 size={18} />
            Papelera{showTrash && trashedTherapists.length ? ` (${trashedTherapists.length})` : ''}
          </Button>
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}
          >
          <DialogTrigger asChild>
            <Button data-testid={THERAPISTS.addBtn} className="btn-primary flex items-center gap-2">
              <Plus size={20} />
              Nuevo Terapeuta
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTherapist ? 'Editar Terapeuta' : 'Nuevo Terapeuta'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <FileUpload
                label="Foto del Terapeuta"
                fileType="photo"
                accept="image/*"
                maxSize={5}
                currentFileId={formData.photo_file_id}
                onFileUploaded={(fileId) => setFormData({ ...formData, photo_file_id: fileId })}
                onFileRemoved={() => setFormData({ ...formData, photo_file_id: null })}
              />
              
              <div>
                <Label htmlFor="name">Nombre Completo *</Label>
                <Input
                  id="name"
                  data-testid={THERAPISTS.nameInput}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  data-testid={THERAPISTS.emailInput}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="specialization">Especialización</Label>
                <Input
                  id="specialization"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  className="mt-1"
                />
              </div>
              <Button type="submit" data-testid={THERAPISTS.saveBtn} className="btn-primary w-full">
                {editingTherapist ? 'Actualizar' : 'Crear'} Terapeuta
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {showTrash && (
        <Card className="p-6 border-amber-200" data-testid="therapists-trash-panel">
          <div className="flex items-center gap-2 mb-1">
            <Trash2 className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-amber-800">Papelera de Terapeutas ({trashedTherapists.length})</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Los terapeutas eliminados se conservan 30 días y luego se borran automáticamente.
          </p>
          {trashedTherapists.length === 0 ? (
            <p className="text-sm text-gray-500">No hay terapeutas en la papelera.</p>
          ) : (
            <div className="space-y-2">
              {trashedTherapists.map((t) => (
                <div key={t.id} className="flex items-center justify-between border rounded-lg px-4 py-3" data-testid={`trash-therapist-${t.id}`}>
                  <div>
                    <p className="font-medium" style={{ color: '#2D3748' }}>{t.name}</p>
                    <p className="text-xs text-gray-500">{t.days_remaining ?? 30} días restantes</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => handleRestore(t.id)}
                      data-testid={`restore-therapist-${t.id}`}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Restaurar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handlePurge(t.id)}
                      data-testid={`purge-therapist-${t.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={THERAPISTS.list}>
        {therapists.map((therapist) => (
          <Card key={therapist.id} className="p-6 hover:-translate-y-1 transition-transform duration-200">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <TherapistAvatar 
                  photoFileId={therapist.photo_file_id} 
                  name={therapist.name} 
                />
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                    {therapist.name}
                  </h3>
                  <p className="text-sm" style={{ color: '#718096' }}>
                    {therapist.email}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditDialog(therapist)}
                  className="hover:bg-[#E5E2DC]"
                >
                  <Edit size={16} style={{ color: '#5A7D6C' }} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(therapist.id)}
                  className="hover:bg-red-50"
                >
                  <Trash2 size={16} style={{ color: '#F44336' }} />
                </Button>
              </div>
            </div>
            {therapist.phone && (
              <p className="text-sm mb-2" style={{ color: '#2D3748' }}>
                Tel: {therapist.phone}
              </p>
            )}
            {therapist.specialization && (
              <div className="mt-2 px-3 py-1 rounded-full inline-block" style={{ backgroundColor: '#E5E2DC' }}>
                <p className="text-xs font-medium" style={{ color: '#5A7D6C' }}>
                  {therapist.specialization}
                </p>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default TherapistsPage;
